import { useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { setJobLength } from '../../../features/jobs/api/jobsApi';

/**
 * Correcting a lot that was launched at the wrong length.
 *
 * The figure is typed by hand at launch and sometimes typed wrong — 20,000 m
 * for a run that made 10,000. Everything downstream believes it: the material
 * plan, the stock-versus-order split, how much a planner thinks is still free
 * to promise. Leaving it wrong quietly poisons the simulation, and deleting and
 * relaunching throws away every execution already recorded against it.
 *
 * The one thing it will not do is shrink a lot below what has already been
 * promised to orders. Those metres belong to somebody, and freeing them is a
 * decision with a customer on the other end of it — not something an edit box
 * should do on the way past.
 */

const fmt = n => Math.round(Number(n) || 0).toLocaleString();

const EditLotLength = ({ project, onDone }) => {
  const queryClient = useQueryClient();
  const jobId = project?.jobId || project?._id;
  const current = Number(project?.plannedLength) || 0;

  const [show, setShow] = useState(false);
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // European decimals: a comma is what half the keyboards in the plant produce.
  const parsed = Math.round(Number(String(value).replace(',', '.')));
  const valid = Number.isFinite(parsed) && parsed > 0 && parsed !== current;

  const promised = (project?.allocations || []).reduce(
    (s, a) => s + (Number(a?.allocatedMeters ?? a?.meters) || 0),
    0
  );

  const open = () => {
    setValue(String(current));
    setReason('');
    setError(null);
    setResult(null);
    setShow(true);
  };

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await setJobLength(jobId, parsed, reason.trim() || null);
      setResult(res);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({
        predicate: q =>
          ['product-allocation', 'token-feasibility'].some(k =>
            String(q.queryKey?.[0] || '').startsWith(k)
          )
      });
      setShow(false);
      if (onDone) onDone(res);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not change it');
    } finally {
      setBusy(false);
    }
  };

  if (!jobId) return null;

  return (
    <>
      <Button
        variant="link"
        size="sm"
        className="p-0 ms-2 align-baseline"
        title="Correct the lot length"
        onClick={open}
      >
        <FontAwesomeIcon icon={faPenToSquare} className="fs-10" />
      </Button>

      {result?.changed && (
        <span className="text-success fs-10 ms-2">
          corrected from {fmt(result.before)} m
        </span>
      )}

      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fs-7">
            Correct the lot length
            <span className="text-body-tertiary fs-9 ms-2">
              {project?.productLabel || project?.jobCode}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="text-700 fs-9">
            The material plan is rebuilt from the recipe and the new length, and
            the stock/order split moves with it. Executions already recorded are
            untouched.
          </p>

          {error && (
            <Alert variant="danger" className="py-2 fs-9">
              {error}
            </Alert>
          )}

          {promised > 0 && (
            <Alert variant="info" className="py-2 fs-9">
              {fmt(promised)} m of this lot is promised to orders — it cannot go
              below that. Take the promises back first if you need to.
            </Alert>
          )}

          <Form.Label className="fs-9 text-700">
            Lot length (m) — currently {fmt(current)}
          </Form.Label>
          <Form.Control
            autoFocus
            type="text"
            inputMode="decimal"
            value={value}
            onChange={e => setValue(e.target.value)}
            className="input-spin-none"
          />

          <Form.Label className="fs-9 text-700 mt-3">
            Reason (optional, kept in the history)
          </Form.Label>
          <Form.Control
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="launched at the wrong length…"
          />
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="phoenix-secondary"
            onClick={() => setShow(false)}
            disabled={busy}
          >
            Leave it
          </Button>
          <Button variant="primary" onClick={run} disabled={!valid || busy}>
            {busy ? (
              <Spinner animation="border" size="sm" />
            ) : (
              `Set to ${valid ? fmt(parsed) : '—'} m`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditLotLength;
